import React, {useEffect, useState} from "react";
import {Alert, Box, Button, CircularProgress, FormControlLabel, Switch, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Refresh as RefreshIcon} from "@mui/icons-material";
import {getCourierOrders} from "./courier-api";
import DeliveryProgressChip from "./delivery-progress-chip";
import CourierOrderHistory from "./courier-order-history";

function CourierOrderRow({order, language, t}) {
    const [historyOpen, setHistoryOpen] = useState(false);
    return <>
        <TableRow>
            <TableCell>{order.orderNumber}</TableCell><TableCell>{order.pharmacyName}</TableCell>
            <TableCell>{t(order.status.charAt(0) + order.status.slice(1).toLowerCase())}</TableCell>
            <TableCell><DeliveryProgressChip status={order.courierReleasedAt ? "UNASSIGNED" : order.deliveryStatus}/></TableCell>
            <TableCell>{order.orderDate ? new Date(order.orderDate).toLocaleDateString(language) : "—"}</TableCell>
            <TableCell>{Number(order.totalPrice).toLocaleString(language)} ֏</TableCell>
            <TableCell>{order.productCount}</TableCell>
            <TableCell><Button size="small" aria-label={`${t("CourierDeliveryTimeline")}: ${order.orderNumber}`}
                aria-expanded={historyOpen} onClick={() => setHistoryOpen(value => !value)}>
                {t("CourierDeliveryTimeline")}</Button></TableCell>
        </TableRow>
        {historyOpen && <TableRow><TableCell colSpan={8}>
            <CourierOrderHistory orderId={order.orderId}/>
        </TableCell></TableRow>}
    </>;
}

export default function CourierOrders({id, version = 0, onRefresh, refreshDisabled = false}) {
    const {t, i18n} = useTranslation();
    const [activeOnly, setActiveOnly] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState({loading: true, data: null, error: ""});
    const language = i18n.language;
    const scope = `${id}:${activeOnly}:${page}:${size}:${language}:${retry}:${version}`;
    useEffect(() => {
        const controller = new AbortController();
        setResult({scope, loading: true, data: null, error: ""});
        getCourierOrders(id, activeOnly, page, size, language, controller.signal).then(data => {
            if (controller.signal.aborted) return;
            if (page > Math.max(0, data.totalPages - 1)) { setPage(Math.max(0, data.totalPages - 1)); return; }
            setResult({scope, loading: false, data, error: ""});
        }).catch(error => {
            if (!controller.signal.aborted) setResult({scope, loading: false, data: null,
                error: error.response?.data?.message || "CourierOrdersLoadFailed"});
        });
        return () => controller.abort();
    }, [id, activeOnly, page, size, language, retry, version, scope]);

    const current = result.scope === scope ? result : {loading: true, data: null, error: ""};

    return <Box sx={{mt: 3}}>
        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap"}}>
            <Typography variant="h6">{t("CourierAssignedOrders")}</Typography>
            <Button startIcon={<RefreshIcon/>} disabled={current.loading || refreshDisabled} onClick={onRefresh || (() => setRetry(value => value + 1))}>
                {t("RefreshCourierOrders")}</Button>
        </Box>
        <FormControlLabel label={t("CourierActiveOrdersOnly")} control={<Switch checked={activeOnly}
            onChange={event => { setActiveOnly(event.target.checked); setPage(0); }}/>}/>
        {current.error ? <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry(value => value + 1)}>
            {t("Retry")}</Button>}>{t(current.error)}</Alert> : current.loading ? <CircularProgress aria-label={t("Loading")}/> : <>
            {current.data.content.length === 0 ? <Typography sx={{py: 2}}>{t("CourierNoAssignedOrders")}</Typography> :
                <TableContainer><Table size="small" aria-label={t("CourierAssignedOrders")}>
                    <TableHead><TableRow>{["OrderNumber", "Pharmacy", "Status", "CourierDeliveryProgressTitle", "OrderDate", "TotalAmount", "Quantity", "CourierDeliveryTimeline"]
                        .map(label => <TableCell key={label}>{t(label)}</TableCell>)}</TableRow></TableHead>
                    <TableBody>{current.data.content.map(order => <CourierOrderRow key={`${scope}:${order.orderId}`}
                        order={order} language={language} t={t}/>)}</TableBody>
                </Table></TableContainer>}
            <TablePagination component="div" count={current.data.totalElements} page={page} rowsPerPage={size}
                             rowsPerPageOptions={[10, 20, 50]} labelRowsPerPage={t("CourierOrdersPerPage")}
                             labelDisplayedRows={({from, to, count}) => t("UserPageRange", {from, to, count})}
                             getItemAriaLabel={type => t(type === "next" ? "NextCourierOrdersPage" : "PreviousCourierOrdersPage")}
                             onPageChange={(_, value) => setPage(value)}
                             onRowsPerPageChange={event => { setSize(Number(event.target.value)); setPage(0); }}/>
        </>}
    </Box>;
}
